import { Group, Stack, Tabs } from '@mantine/core';
import type {
	DataAccessRequestOut,
	DataAccessRequestStatus,
} from '@repo/api-codegen';
import {
	useListAccessRequests,
	useSuspenseListAccessRequests,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import TabsList from '@repo/common/components/TabsList';
import { UserRole } from '@repo/common/enums/UserRole';
import { captureError } from '@repo/common/utils/web-tracing';
import { Button } from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthUser, useSuspenseFeatureAccess } from '../../api';
import { AddRequestButton } from '../../components/DataAccessRequest/AddRequestButton';
import { EmptyState } from '../../components/EmptyState';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { FilterOptionType } from '../../components/Filter';
import { FILTER_OPTIONS_CONFIG } from '../../components/Filter/constants';
import { TableV2 } from '../../components/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { RequestsOverviewTabController } from '../../features/Overview/RequestsOverviewTabController';
import type { MetricCardType } from '../../features/Overview/types';
import { useListPageTabNavigate } from '../../utils/hook/useTabNavigate';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import { useActions } from './useActions';
import { useColumns } from './useColumns';

type RequestPageTab = 'requests' | 'requests/overview';

const FILTER_OPTIONS = [
	FILTER_OPTIONS_CONFIG[FilterOptionType.REQUEST_STATUS],
	FILTER_OPTIONS_CONFIG[FilterOptionType.CREATED_BY],
];

const METRIC_FILTERS: Partial<
	Record<
		MetricCardType,
		Array<{
			value: string;
			operator: string;
			filterType: string;
		}>
	>
> = {
	Open: [
		{
			value: 'pending',
			operator: 'is',
			filterType: 'status',
		},
	],
	Active: [
		{
			value: 'approved',
			operator: 'is',
			filterType: 'status',
		},
	],
	Denied: [
		{
			value: 'rejected',
			operator: 'is',
			filterType: 'status',
		},
	],
	Closed: [
		{
			value: 'cancelled',
			operator: 'is',
			filterType: 'status',
		},
	],
};

const COLUMN_VISIBILITY = {
	catalogServerType: 'data_access_requests',
	catalogType: 'data_access_requests',
} as const;

function getQuickActions(status: DataAccessRequestStatus | undefined) {
	if (status === 'pending') {
		return ['actions::approve', 'actions::reject', 'actions::delete'] as const;
	} else if (status === 'approved') {
		return ['actions::cancel'] as const;
	} else if (
		status === 'rejected' ||
		status === 'expired' ||
		status === 'cancelled'
	) {
		return ['actions::no_actions_available'] as const;
	}
	return ['actions::no_actions_available'] as const;
}

function AccessRequestsTable() {
	const navigate = useNavigate();
	const columns = useColumns();
	const actions = useActions();
	const [searchParams] = useSearchParams();

	const [selectedRecordsState, setSelectedRecordsState] = useState<{
		selectedRecords: DataAccessRequestOut[];
		lastSelectedIndex: number | null;
	}>({
		selectedRecords: [],
		lastSelectedIndex: null,
	});

	const defaultRequiredSearchParams = useMemo(() => {
		const filters = new URLSearchParams(window.location.search).get('filters');

		if (filters) {
			try {
				const parsedFilters = JSON.parse(filters);
				const params: Record<string, string> = {};

				const statusFilter = parsedFilters.find(
					(f: { filterType: string; value?: string }) =>
						f.filterType === 'status'
				);
				if (statusFilter?.value) {
					params.status = statusFilter.value;
				}

				const createdByFilter = parsedFilters.find(
					(f: { filterType: string; value?: string | string[] }) =>
						f.filterType === 'created_by'
				);
				if (createdByFilter?.value) {
					if (Array.isArray(createdByFilter.value)) {
						params.created_by = createdByFilter.value.join(',');
					} else if (typeof createdByFilter.value === 'string') {
						params.created_by = createdByFilter.value;
					}
				}

				return params;
			} catch (err) {
				captureError(err);
			}
		}
		return {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams, window.location.search]);

	const handleClick = useCallback(
		(cell) => {
			if (
				(cell.column as ExtendedDataTableColumn<DataAccessRequestOut>)
					.navigate !== false
			) {
				navigate(`/requests/${cell.record.id}`);
			}
		},
		[navigate]
	);

	const handleSelectedRecordsStateChange = useCallback(
		(newState: {
			selectedRecords: DataAccessRequestOut[];
			lastSelectedIndex: number | null;
		}) => {
			setSelectedRecordsState(newState);
		},
		[]
	);

	const isRecordSelectable = useCallback(
		(record: DataAccessRequestOut) => {
			const [initialSelectedRecord] = selectedRecordsState.selectedRecords;
			const selectedRequestStatus = initialSelectedRecord?.status;
			const recordStatus = record?.status;
			if (!selectedRequestStatus || !recordStatus) return true;
			return selectedRequestStatus === recordStatus;
		},
		[selectedRecordsState]
	);

	const quickActions = useMemo(() => {
		const selectedStatuses = new Set(
			selectedRecordsState.selectedRecords.map((record) => record.status)
		);

		let parsedStatus: DataAccessRequestStatus | undefined = undefined;

		if (selectedStatuses.has('pending')) {
			parsedStatus = 'pending';
		} else if (selectedStatuses.has('approved')) {
			parsedStatus = 'approved';
		} else if (selectedStatuses.has('rejected')) {
			parsedStatus = 'rejected';
		} else if (selectedStatuses.has('expired')) {
			parsedStatus = 'expired';
		} else if (selectedStatuses.has('cancelled')) {
			parsedStatus = 'cancelled';
		} else if (selectedStatuses.has('deleted')) {
			parsedStatus = 'deleted';
		}

		return getQuickActions(parsedStatus);
	}, [selectedRecordsState.selectedRecords]);

	return (
		<TableV2
			pluralTypeString="requests"
			withQuickActions={quickActions}
			columns={columns}
			columnVisibility={COLUMN_VISIBILITY}
			defaultRequiredSearchParams={defaultRequiredSearchParams}
			// @ts-expect-error using API codegen list hook
			usePaginationList={useListAccessRequests}
			useCodegenListInterface
			withActions={actions}
			withCheckbox={true}
			withCsvExport
			withInteractiveHeader
			withFilterOptions={FILTER_OPTIONS}
			withSearch
			defaultSort={null}
			onCellClick={handleClick}
			queryParams={defaultRequiredSearchParams}
			onSelectedRecordsStateChange={handleSelectedRecordsStateChange}
			isRecordSelectable={isRecordSelectable}
			withHeaderCheckbox={false}
		/>
	);
}

export default function RequestsPage() {
	const { user, workspace } = useAuthUser();
	const { classes, theme } = useStyles();
	const navigateToTab = useListPageTabNavigate();
	const navigate = useNavigate();

	const { darAccess } = useSuspenseFeatureAccess();

	const [activeTab, setActiveTab] = useState<RequestPageTab>(() => {
		const storedTab = localStorage.getItem('requests-tab');
		const urlParams = new URLSearchParams(window.location.search);
		// If there are filters in the URL, default to requests tab
		if (urlParams.has('filters')) {
			return 'requests';
		}
		// Otherwise use stored tab or default to requests
		return (storedTab as RequestPageTab) || 'requests';
	});

	const { data: pendingRequests, isLoading: isLoadingPending } =
		useSuspenseListAccessRequests({
			queryParams: { status: 'pending' },
		});
	const { data: activeRequests, isLoading: isLoadingActive } =
		useSuspenseListAccessRequests({
			queryParams: { status: 'approved' },
		});
	const { data: expiredRequests, isLoading: isLoadingExpired } =
		useSuspenseListAccessRequests({
			queryParams: { status: 'expired' },
		});
	const { data: rejectedRequests, isLoading: isLoadingRejected } =
		useSuspenseListAccessRequests({
			queryParams: { status: 'rejected' },
		});
	const { data: cancelledRequests, isLoading: isLoadingCancelled } =
		useSuspenseListAccessRequests({
			queryParams: { status: 'cancelled' },
		});

	const tabs = useMemo(
		() => [
			{ value: 'overview', label: 'Overview' },
			{ value: 'requests', label: 'Requests' },
		],
		[]
	);

	const handleMetricCardClick = useCallback(
		(metricType: MetricCardType) => {
			const filters = METRIC_FILTERS[metricType];
			if (!filters) return;

			navigate(
				`/requests?filters=${encodeURIComponent(JSON.stringify(filters))}`
			);
			setActiveTab('requests');
			trackEvent(
				'requests/overview/metric_card/click',
				{
					metric_type: metricType,
				},
				user,
				workspace!
			);
		},
		[navigate, setActiveTab, user, workspace]
	);

	if (
		!pendingRequests?.count &&
		!isLoadingPending &&
		!activeRequests?.count &&
		!isLoadingActive &&
		!expiredRequests?.count &&
		!isLoadingExpired &&
		!rejectedRequests?.count &&
		!isLoadingRejected &&
		!cancelledRequests?.count &&
		!isLoadingCancelled
	) {
		return (
			<EmptyState
				className={classes.emptySpace}
				imgSrc="/images/illustrations/requests.webp"
				imgWidth={theme.other.space[40]}
				title="Access Requests"
				description="Request access and manage permissions to data in an end-to-end workflow."
				includeGoBack={false}
				stateHeight="80vh"
				size="lg"
				withActions={
					<Group>
						<Button
							component="a"
							target="_blank"
							rel="noreferrer noopener"
							size="md"
							href="https://docs.secoda.com/features/access-requests"
							leftIconName="externalLink"
						>
							Learn more
						</Button>
						<AddRequestButton />
					</Group>
				}
			/>
		);
	}

	const handleTabChange = (value: string): void => {
		if (value === 'requests' || value === 'overview') {
			setActiveTab(value as RequestPageTab);
			localStorage.setItem('requests-tab', value);
			// Only navigate if we're not already on the requests page with filters
			if (
				!(value === 'requests' && window.location.search.includes('filters'))
			) {
				navigateToTab(value);
			}
		}
		trackEvent(
			'requests/tabs/click',
			{
				tab: value,
			},
			user,
			workspace!
		);
	};

	return (
		<FeatureGuard
			featureName="Data Access Requests"
			description="Request access and manage permissions to data in an end-to-end workflow."
			v1AllowedRoles={[
				UserRole.ADMIN,
				UserRole.EDITOR,
				UserRole.VIEWER,
				UserRole.GUEST,
			]}
			v2Permission="Integrations.Read"
			isFeatureIncludedInPlan={darAccess ?? false}
			isAlwaysIncludedFeature={false}
		>
			<Stack className={classes.featureWrapper}>
				<Tabs
					value={activeTab}
					onTabChange={handleTabChange}
					keepMounted={false}
				>
					<TabsList mb="sm" tabs={tabs} />
					<Tabs.Panel value="overview">
						<RequestsOverviewTabController
							onMetricCardClick={handleMetricCardClick}
						/>
					</Tabs.Panel>
					<Tabs.Panel value="requests">
						<AccessRequestsTable />
					</Tabs.Panel>
				</Tabs>
			</Stack>
		</FeatureGuard>
	);
}
